/**
 * User state - represents the complete user state in the widget
 */
export interface UserState {
  /** Random generated ID only used to signal that a user has been created */
  id: string | null;
  /** The type of user that is signed in */
  type: UserType | null;
  /** Optional email address of the user */
  email: string;
  /** Optional phone number of the user */
  phoneNumber: string;
  /** Cohort ID used for percentage rollout throttling */
  cohortId: string | null;
  /** Gladly customer ID of the user */
  gladlyCustomerId: string | null;
  /** User has given us email address => true, anonymous user => false */
  hasOnboarded: boolean;
  /** Optional name of the user */
  name: string;
  /** userId of the user within Chat Provider, also known as customerAddress */
  userId: string | null;
  /** Chat Session JWT used to authenticate with chat provider */
  token: string | null;
  /** A callback function used to obtain a JWT to authenticate with chat provider */
  hasUserIdentityJwtCallback: boolean;
  /** User identity JWT that identifies the user and can be used to get a chat session JWT */
  userIdentityJwt: string | null;
  /** Identity value when signed in with User Identity JWT */
  identity: string | null;
  /** Identity type when signed in with User Identity JWT */
  identityType: UserIdentityType | null;
}

/**
 * Initial user state - used to initialize the user state
 */
export function getInitialUserState(): UserState {
  return {
    cohortId: null,
    id: null,
    type: null,
    email: '',
    phoneNumber: '',
    gladlyCustomerId: null,
    hasOnboarded: false,
    name: '',
    userId: null,
    token: null,
    hasUserIdentityJwtCallback: false,
    userIdentityJwt: null,
    identity: null,
    identityType: null,
  };
}

/**
 * Public User object - what gets returned from getUser() API
 * This matches the public API contract from GladlySidekick.js
 */
export interface PublicUser {
  /** User name */
  name: string;
  /** The type of user */
  type: UserType;
  /** The type of identity of the user */
  identityType: UserIdentityType;
  /** The identity value of the user based on the identity type */
  identity: string;
  /** TRUE if user has an open conversation */
  hasOpenConversation: boolean;
}

/**
 * User type - represents the authentication state of the user
 */
export const UserType = {
  /** User is anonymous, there is no identity of the user */
  ANONYMOUS: 'ANONYMOUS',
  /** User is authenticated with a User Identity JWT */
  AUTHENTICATED: 'AUTHENTICATED',
  /** User is self identified via onboarding or setUser without a JWT */
  SELF_IDENTIFIED: 'SELF_IDENTIFIED',
} as const;

export type UserType = (typeof UserType)[keyof typeof UserType];

/**
 * User identity type - represents how the user is identified
 */
export const UserIdentityType = {
  EMAIL: 'EMAIL',
  USERID: 'USERID',
  MOBILE_PHONE_NUMBER: 'MOBILE_PHONE_NUMBER',
} as const;

export type UserIdentityType = (typeof UserIdentityType)[keyof typeof UserIdentityType];

/**
 * User credentials - token and userId for chat provider authentication
 */
export interface UserCredentials {
  /** Chat Session JWT used to authenticate with chat provider */
  token: string | null;
  /** userId of the user within Chat Provider, also known as customerAddress */
  userId: string | null;
}

/**
 * Type guard to check if user exists (has any identifying information)
 */
export function hasUser(user: UserState): boolean {
  return !!(user.name || user.email || hasUserCredentials(user) || user.hasUserIdentityJwtCallback);
}

/**
 * Type guard to check if user has credentials
 */
export function hasUserCredentials(user: UserState): boolean {
  return !!(user.userId && user.token);
}

/**
 * Type guard to check if user is authenticated but hasn't started a session
 * This represents a user that has been set via setUser with a jwt but has not started a session
 */
export function isAuthenticatedWithoutId(user: UserState): boolean {
  return user.type === UserType.AUTHENTICATED && !user.id;
}

/**
 * Get user credentials from user state
 */
export function getUserCredentials(user: UserState): UserCredentials {
  return {
    token: user.token,
    userId: user.userId,
  };
}
